import { toast } from "sonner"

export interface UndoToastOptions {
  /** Message shown in the toast */
  message: string | ((secondsRemaining: number) => string)
  /** Seconds before the action executes (default: 5) */
  duration?: number
  /** Called when countdown finishes without undo */
  onConfirm: () => void
  /** Called when user clicks Undo */
  onUndo?: () => void
  /** Label for the undo button (default: "Undo") */
  undoLabel?: string
}

/**
 * Show an undo toast with a visual countdown progress bar.
 * Returns a dismiss function.
 *
 * ```ts
 * showUndoToast({
 *   message: (s) => `Rejecting 3 items (${s}s)`,
 *   onConfirm: () => rejectItems(),
 *   onUndo: () => toast("Action undone"),
 * })
 * ```
 */
export function showUndoToast({
  message,
  duration = 5,
  onConfirm,
  onUndo,
  undoLabel = "Undo",
}: UndoToastOptions): () => void {
  const safeDuration = Math.max(1, Math.round(duration))
  const toastId = `undo-${Date.now()}`
  let remaining = safeDuration
  let timer: ReturnType<typeof setInterval> | null = null
  let stopped = false

  const dismiss = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    toast.dismiss(toastId)
  }

  const getText = () =>
    typeof message === "function" ? message(remaining) : message

  const render = () => {
    const progress = remaining / safeDuration

    toast.custom(
      () => (
        <div className="relative flex w-[var(--width)] items-center gap-3 overflow-hidden rounded-xl border border-border bg-popover px-4 py-3 shadow-lg">
          <p className="flex-1 min-w-0 text-sm font-semibold text-popover-foreground">
            {getText()}
          </p>
          <button
            type="button"
            onClick={() => {
              stopped = true
              dismiss()
              onUndo?.()
            }}
            className="shrink-0 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background transition-opacity hover:opacity-90"
          >
            {undoLabel}
          </button>
          <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden rounded-b-xl">
            <div
              className="h-full bg-primary transition-[width] duration-1000 ease-linear"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      ),
      {
        id: toastId,
        duration: Infinity,
        unstyled: true,
      },
    )
  }

  render()

  timer = setInterval(() => {
    remaining -= 1
    render()
    if (remaining <= 0) {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
      setTimeout(() => {
        if (!stopped) {
          toast.dismiss(toastId)
          onConfirm()
        }
      }, 400)
    }
  }, 1000)

  return dismiss
}
